import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { PublicApiService } from '../../services/public-api.service';
import { ClampDefinitionService } from '../../services/clamp-definition.service';
import { Fleet } from '../../models/fleet.model';

// Extended Fleet interface with level information for hierarchical display
interface FleetWithLevel extends Fleet {
  level: number;
}

// Interface for the fleet lookup map
interface FleetLookupMap {
  [uuid: string]: string;
}

@Component({
  selector: 'app-fleet-selector',
  templateUrl: './fleet-selector.component.html',
  styleUrls: ['./fleet-selector.component.scss']
})
export class FleetSelectorComponent implements OnInit {
  @Output() fleetSelected = new EventEmitter<Fleet>();

  fleets: Fleet[] = [];
  flattenedFleets: FleetWithLevel[] = [];
  selectedFleet: Fleet | null = null;
  loading = false;
  error = '';
  fleetLookupMap: FleetLookupMap = {};

  constructor(
    private apiService: PublicApiService,
    private clampDefinitionService: ClampDefinitionService
  ) {}

  ngOnInit(): void {
    this.loadFleets();
  }

  /**
   * Flattens a hierarchical structure of fleets into a single-level array with level information
   * @param fleets The hierarchical structure of fleets
   * @returns A flattened array of fleets with level information
   */
  flattenFleets(fleets: Fleet[]): FleetWithLevel[] {
    const result: FleetWithLevel[] = [];
    const processFleet = (fleet: Fleet, level: number) => {
      // Add the fleet with its level to the result
      result.push({
        ...fleet,
        level
      });

      // Process children with an increased level
      if (fleet.children && fleet.children.length > 0) {
        fleet.children.forEach(child => processFleet(child, level + 1));
      }
    };

    fleets.forEach(fleet => processFleet(fleet, 0));
    return result;
  }

  loadFleets(): void {
    this.loading = true;
    this.error = '';

    this.apiService.getUserFleets().subscribe({
      next: (response) => {
        console.log('API Response:', response);

        // Check if response has a data property
        if (response.data) {
          this.fleets = response.data;
        } else {
          // If response is an array, use it directly
          this.fleets = Array.isArray(response) ? response : [];
          console.log('Processed fleets:', this.fleets);
        }

        // Flatten the hierarchical structure of fleets for display in the dropdown
        this.flattenedFleets = this.flattenFleets(this.fleets);
        console.log('Flattened fleets:', this.flattenedFleets);

        // Populate the lookup map with fleet UUIDs and IDs
        this.fleetLookupMap = {};
        this.flattenedFleets.forEach(fleet => {
          this.fleetLookupMap[fleet.uuid] = fleet.id;
        });
        console.log('Fleet lookup map:', this.fleetLookupMap);

        // Initialize clamp definitions after fleets are loaded
        this.initializeClampDefinitions();

        this.loading = false;

        // If there's only one fleet, select it automatically
        if (this.flattenedFleets.length === 1) {
          this.selectFleet(this.flattenedFleets[0]);
        }
      },
      error: (err) => {
        this.error = 'Failed to load fleets. Please try again.';
        this.loading = false;
        console.error('Error loading fleets:', err);
      }
    });
  }

  selectFleet(fleet: Fleet): void {
    this.selectedFleet = fleet;
    this.fleetSelected.emit(fleet);
  }

  /**
   * Gets the fleet ID for a given UUID
   * @param uuid The UUID of the fleet
   * @returns The ID of the fleet, or the UUID if not found
   */
  getFleetIdByUuid(uuid: string): string {
    return this.fleetLookupMap[uuid] || uuid;
  }

  /**
   * Initialize the clamp definitions lookup table
   */
  private initializeClampDefinitions(): void {
    // Only initialize if not already loaded or loading
    if (!this.clampDefinitionService.isLoaded() && !this.clampDefinitionService.isLoading()) {
      console.log('Initializing clamp definitions...');

      this.clampDefinitionService.initializeClampDefinitions().subscribe({
        next: (success) => {
          if (success) {
            console.log('Clamp definitions initialized successfully');
          } else {
            console.error('Failed to initialize clamp definitions:', this.clampDefinitionService.getError());
          }
        },
        error: (err) => {
          console.error('Error initializing clamp definitions:', err);
        }
      });
    }
  }
}
